import { parseUtc } from "./parseUtc";

/**
 * Where a sensor has been, derived from its installation records.
 *
 * WHY THIS EXISTS: every telemetry read is keyed on the sensor's serial alone,
 * so a sensor that moves carries its whole history to the new location. Counts,
 * trends and building rollups then describe two kitchens at once. The only
 * record of a move is `cts_scs_installations`, which the API exposes at
 * `GET /api/installations/{scsName}` (see `getInstallations`).
 *
 * WHAT THIS CANNOT TELL YOU, and no client-side work can fix:
 *
 *   1. A move made by editing sensor-group membership directly
 *      (`POST/DELETE /api/sensor-groups/{id}/sensors`) writes no installation
 *      row. For such a sensor the cutoff below is TOO EARLY and old-location
 *      data still shows. Do not describe the cutoff as scoping all data.
 *   2. Which building a past segment was. The row's `address` is flattened
 *      server-side to `"<groupName> - <displayName>"` and `sensorGroupId` is
 *      not persisted, so a segment is a label, never a resolvable building.
 *   3. Whether two installs at one physical place were spelled identically.
 *      Coalescing (below) compares the address text, so a group or unit renamed
 *      between two installs reads as a move that never happened.
 *
 * Consumers disclose (1) in the UI rather than only here, because it changes
 * how a reader should read every number on the page.
 */

/** One row of `GET /api/installations/{scsName}`, as the API sends it. */
export type InstallationRow = {
  name?: string;
  /** ISO 8601. Server-stamped `DateTime.UtcNow`, so genuinely UTC -- but it
   *  round-trips through DynamoDB and arrives with no `Z`. Parse with parseUtc. */
  dateInstallation?: string;
  address?: string | null;
  username?: string;
  deviceId?: string | null;
  location?: { latitude: number; longitude: number } | null;
};

export type TenureSegment = {
  /** Display label, as the first install of this segment spelled it. NOT
   *  resolvable to a sensor group -- see the module note. Null when the row
   *  carried no address. */
  address: string | null;
  /** Epoch ms of the install that began this segment. */
  startedAt: number;
  /** One ms before the next segment began; null for the segment in progress. */
  endedAt: number | null;
  /** Installs folded into this segment. >1 means the sensor was re-installed at
   *  the same place -- a repair or a re-provisioning, not a move. */
  rowCount: number;
};

/**
 * Why we do or do not have a cutoff. The two failure reasons must stay
 * distinguishable: a read that FAILED is not a sensor with no install record,
 * and telling a user "no installation record" because a request timed out is a
 * claim about the sensor that we have not earned.
 */
export type TenureReason = "known" | "no-records" | "fetch-failed" | "unresolved";

export type TenureScope = {
  /**
   * Telemetry at or after this instant belongs to the sensor's current
   * location. NULL means we cannot say -- callers must then show everything
   * and disclose it, never show nothing.
   */
  cutoffMs: number | null;
  reason: TenureReason;
  /** Oldest first. Empty unless `reason` is "known". */
  segments: TenureSegment[];
};

/** Before the installations query settles. Queries gate on it, so it should
 *  never reach a disclosure -- it exists so "loading" is not spelled the same
 *  way as "this sensor has no install record". */
export const TENURE_UNRESOLVED: TenureScope = {
  cutoffMs: null,
  reason: "unresolved",
  segments: [],
};

/** The installations read failed. Fail OPEN: show the full history, and say
 *  that this is a loading problem rather than a fact about the sensor. */
export const TENURE_FETCH_FAILED: TenureScope = {
  cutoffMs: null,
  reason: "fetch-failed",
  segments: [],
};

/** Nothing has ever been recorded for this sensor. Also fail open. */
export const TENURE_NO_RECORDS: TenureScope = {
  cutoffMs: null,
  reason: "no-records",
  segments: [],
};

/**
 * Fold whitespace and case so that "Maple Court -  Apt 3" and
 * "maple court - Apt 3" are one place. Deliberately nothing cleverer: in
 * particular we never split on " - " to recover the group name, because a
 * group name may itself contain a hyphen and the separator is ambiguous.
 */
function normaliseAddress(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const collapsed = raw.trim().replace(/\s+/g, " ");
  return collapsed.length === 0 ? null : collapsed.toLowerCase();
}

function displayAddress(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const collapsed = raw.trim().replace(/\s+/g, " ");
  return collapsed.length === 0 ? null : collapsed;
}

/**
 * Installation rows -> tenure segments and the current-location cutoff.
 *
 * Consecutive installs at the same address fold into ONE segment, and the
 * cutoff is that segment's FIRST install, not the newest row. This is the whole
 * point: a technician re-provisioning a sensor in place would otherwise reset
 * its visible history to the moment of the repair.
 *
 * Two rows with no address at all fold together as well. That errs toward
 * keeping history rather than truncating it, which is the right way to be wrong
 * here -- the page discloses that figures may span locations, and hiding real
 * safety data is worse than showing extra with a caveat.
 *
 * Rows whose timestamp will not parse are skipped rather than thrown on, the
 * same posture the report counters take toward malformed telemetry.
 */
export function deriveTenure(rows: unknown): TenureScope {
  if (!Array.isArray(rows)) return TENURE_NO_RECORDS;

  const parsed: { at: number; key: string | null; label: string | null }[] = [];
  for (const row of rows as InstallationRow[]) {
    const at = parseUtc(row?.dateInstallation);
    if (at === null) continue;
    parsed.push({
      at,
      key: normaliseAddress(row?.address),
      label: displayAddress(row?.address),
    });
  }

  if (parsed.length === 0) return TENURE_NO_RECORDS;

  // The endpoint answers newest-first and we concatenate pages, so ordering is
  // the repository's business, not ours. Sort rather than trust it.
  parsed.sort((a, b) => a.at - b.at);

  const segments: TenureSegment[] = [];
  let openKey: string | null = null;
  for (const entry of parsed) {
    const open = segments[segments.length - 1];
    if (open !== undefined && openKey === entry.key) {
      open.rowCount += 1;
      continue;
    }
    segments.push({
      address: entry.label,
      startedAt: entry.at,
      endedAt: null,
      rowCount: 1,
    });
    openKey = entry.key;
  }

  // Each segment ends one ms before the next begins, so no instant belongs to
  // two segments -- the same rule weeklyWindow uses for its prior period.
  for (let i = 0; i < segments.length - 1; i += 1) {
    segments[i].endedAt = segments[i + 1].startedAt - 1;
  }

  return {
    cutoffMs: segments[segments.length - 1].startedAt,
    reason: "known",
    segments,
  };
}

/**
 * Split a sensor's tenures into the ones this organization can claim and a
 * count of the ones it cannot.
 *
 * WHY: a sensor's installation and telemetry history follows the SERIAL, not
 * the owner. `cts_scs_installations` and `cts_scs_telemetry` are keyed on the
 * sensor name with no tenant column, and `GET /api/installations/{name}` checks
 * only that the sensor is in your tenant TODAY before returning every row it
 * has. So a refurbished or resold unit arrives carrying the previous owner's
 * property name, unit number, installer email and GPS fix, and this is the
 * layer that stops a dashboard printing them.
 *
 * HOW, and its limits. The only tenant-scoped thing a client holds is the
 * organization's own sensor-group list (`cts_sensor_groups` IS keyed on
 * tenantId), and an installation row records its location as
 * `"<groupName> - <displayName>"`. So ownership is decided by matching that
 * prefix. That is best-effort, and wrong in three directions:
 *
 *   - a group renamed since the install no longer matches, so an organization
 *     can lose sight of its own earlier tenure;
 *   - two organizations with a property of the same name are indistinguishable;
 *   - and, because a display name may itself contain " - " ("Apt 101 - Kitchen"
 *     is a real one), a property name that is a PREFIX of another organization's
 *     claims it. An organization owning "Elm Court" matches a row written as
 *     "Elm Court - North - Apt 1", which is undecidable from the string alone:
 *     it parses equally well as group "Elm Court", unit "North - Apt 1". This is
 *     pinned by a test rather than left to be rediscovered.
 *
 * It therefore fails CLOSED: anything not positively attributable is withheld
 * and counted, never shown. Hiding your own history is a nuisance; showing
 * somebody else's is a disclosure. Only a `tenantId` on the installation row
 * can make this exact, and nothing here should be described as a security
 * boundary -- the API still serves the full history to any caller.
 */
function addressBelongsTo(
  address: string | null,
  ownedGroupNames: string[]
): boolean {
  const normalised = normaliseAddress(address);
  if (normalised === null) return false;
  // No group list yet (still loading, or the read failed) is not the same as an
  // empty one. Either way we cannot attribute anything, so `some` over an empty
  // list correctly returns false and the caller withholds.
  return ownedGroupNames
    .map((name) => normaliseAddress(name))
    .filter((name): name is string => name !== null)
    .some((name) => normalised === name || normalised.startsWith(`${name} - `));
}

export function partitionSegmentsByOwner(
  segments: TenureSegment[],
  ownedGroupNames: string[]
): { owned: TenureSegment[]; withheld: number } {
  const kept = segments.filter((segment) =>
    addressBelongsTo(segment.address, ownedGroupNames)
  );
  return { owned: kept, withheld: segments.length - kept.length };
}

/**
 * Whether the tenure the sensor is in RIGHT NOW is one this organization can
 * claim -- and therefore whether the cutoff means what the page implies.
 *
 * This is the question the figures depend on, not just the history table. A
 * sensor transferred between organizations by reassigning its sensor group
 * writes no installation row (`AddSensorToSensorGroup` mutates membership and
 * nothing else), so the newest row on file can be the PREVIOUS owner's. The
 * cutoff then lands on their install, every count above is scoped to their
 * tenure, and `reason` is still "known" -- a confident claim about somebody
 * else's kitchen.
 *
 * Returns false when we cannot say, which is the same answer as "not ours": the
 * caller qualifies the figures rather than suppressing them, because a sensor
 * whose property was merely renamed must not have its dashboard blanked.
 */
export function currentTenureIsOwned(
  scope: TenureScope,
  ownedGroupNames: string[]
): boolean {
  if (scope.reason !== "known" || scope.segments.length === 0) return false;
  const current = scope.segments[scope.segments.length - 1];
  return addressBelongsTo(current.address, ownedGroupNames);
}

/**
 * Cache-key element for a scope.
 *
 * Every query whose CONTENTS depend on the cutoff must carry this in its
 * react-query key. Without it, the first mount (tenure not yet resolved, so
 * full history) writes unscoped rows into the shared entry and the later scoped
 * read is a cache HIT on unscoped data -- silently, and worst on the portfolio
 * page where forty sensors resolve at different moments.
 *
 * "full" covers every reason with no cutoff, because the rows those produce are
 * identical: unclipped. So they share one generation, and a successful retry
 * moves to a different key on its own.
 */
export function scopeKeyOf(scope: TenureScope): string {
  return scope.cutoffMs == null ? "full" : String(scope.cutoffMs);
}
